// =============================================================================
// TASK-9868 — ticket document metadata: WHO uploaded a file and WHAT it is.
//
// One place that knows the document types and how the uploader stamp is built
// and shown, so the office upload (billing.js review modal) and the driver
// upload (jobboard.js Build Ticket photos) write the same shape and every
// screen reads it the same way. Pure: no DOM, no Firestore, no module state.
//
// Stored on a ticket documents[] row (office) or photos[] row (driver):
//   uploadedBy: { uid, name, personId? }   — from the signed-in session, never typed
//   docType:    'bol' | 'scale_ticket' | 'permit' | 'other'
//
// Rows written before TASK-9868 carry neither field. They render exactly as
// before: no uploader line, no type chip, no print label. Nothing is guessed.
// =============================================================================

export const TICKET_DOC_TYPES = Object.freeze([
  Object.freeze({ value: 'bol', label: 'BOL' }),
  Object.freeze({ value: 'scale_ticket', label: 'Scale ticket' }),
  Object.freeze({ value: 'permit', label: 'Permit' }),
  Object.freeze({ value: 'other', label: 'Other' }),
]);

export const TICKET_DOC_TYPE_DEFAULT = 'other';

const _byValue = new Map(TICKET_DOC_TYPES.map((t) => [t.value, t]));

/** A picked value → a known type; anything else (blank, junk) → 'other'. */
export function normalizeTicketDocType(v) {
  const s = String(v == null ? '' : v).trim().toLowerCase();
  return _byValue.has(s) ? s : TICKET_DOC_TYPE_DEFAULT;
}

/**
 * Label for a stored row's type. '' when the row has no docType at all (a
 * pre-TASK-9868 row shows no chip). An unrecognised stored value reads 'Other'.
 */
export function ticketDocTypeLabel(docType) {
  if (docType == null || docType === '') return '';
  return _byValue.get(normalizeTicketDocType(docType)).label;
}

/** Label to PRINT on the invoice packet. 'Other' and missing print nothing. */
export function ticketDocPrintLabel(docType) {
  if (docType == null || docType === '') return '';
  const t = normalizeTicketDocType(docType);
  return t === TICKET_DOC_TYPE_DEFAULT ? '' : _byValue.get(t).label;
}

/**
 * <option> list for a type picker. Labels are constants — no escaping needed.
 * `selected` null/'' = nothing selected (the caller supplies a placeholder).
 */
export function ticketDocTypeOptionsHtml(selected) {
  const sel = (selected == null || selected === '') ? null : normalizeTicketDocType(selected);
  return TICKET_DOC_TYPES
    .map((t) => `<option value="${t.value}"${t.value === sel ? ' selected' : ''}>${t.label}</option>`)
    .join('');
}

/**
 * The uploader stamp. Every value comes from the signed-in session (auth uid,
 * the server-validated PIN driver name / personId, the member's display name),
 * never from a text box. Returns null when there is no uid — a row with no
 * provable uploader stores nothing rather than a guess.
 */
export function buildTicketDocUploader({ uid, name, personId } = {}) {
  const u = String(uid == null ? '' : uid).trim();
  if (!u) return null;
  const out = { uid: u.slice(0, 128), name: String(name == null ? '' : name).trim().slice(0, 80) };
  const p = String(personId == null ? '' : personId).trim();
  if (p) out.personId = p.slice(0, 128);
  return out;
}

// =============================================================================
// TASK-9958 — FILE PROVENANCE: who put a file on a job or ticket, and HOW.
//
// Chris, 2026-10-19, on a Montana trip-permit receipt sitting on ticket #100898:
// "how did that file get there?" The entry had id/name/urls/uploadedAt and
// nothing else, so the answer had to be dug out of Storage hashes and audit
// timestamps. Every file added to jobs.permits / jobs.photos / tickets.documents
// / tickets.photos (and ticketLines.photos) now carries, from the session:
//   uploadedBy:  { uid, name, personId? }   (same shape as TASK-9868)
//   uploadedVia: one of FILE_ADD_VIA below   (HOW it got there)
//   uploadedAt:  ISO time                    (already there)
// …and each add writes one auditLog FILE_ADDED row (fileAddedAuditPayload).
//
// 🔴 `uploadedVia`, NOT `source`. On a ticket documents[] row `source` already
// means bridge provenance ('upload' | 'job_permit' | 'job_photo' |
// 'clock_photo') and the stale-bridge self-heal keys off it. The server's
// Claude upload link (functions/src/services/jobFileUpload.js) keeps its
// `source: 'claude-upload-link'` and ALSO writes uploadedBy/uploadedVia.
//
// TRUST. The entry stamp is a display convenience: Firestore rules cannot
// inspect each element of an unbounded array, so it is not rule-pinned. The
// rule-pinned records are (a) the auditLog row (`user` + `uid` pinned to the
// caller, immutable) and (b) the Storage object's customMetadata
// `uploadedByUid` (storage.rules pins it to request.auth.uid when present).
// Old rows are never back-filled: no stamp = nothing shown.
// =============================================================================

export const FILE_ADD_VIA = Object.freeze({
  job_modal: 'Job form',
  ticket_edit: 'Ticket edit',
  driver_app: 'Driver app',
  clock_out: 'Clock-out',
  claude_link: 'Claude upload link',
  email_import: 'Email import',
});

/** A known via key, or 'other' for anything else (never a free string). */
export function normalizeFileVia(v) {
  const s = String(v == null ? '' : v).trim();
  return Object.prototype.hasOwnProperty.call(FILE_ADD_VIA, s) ? s : 'other';
}

/**
 * Human label for HOW a stored row got there. Reads the new `uploadedVia`, and
 * the two explicit provenance fields older writers already stored (the Claude
 * link's `source` and AP billing's `uploadedFrom`) — recorded facts, not guesses.
 * '' when the row says nothing.
 */
export function fileViaLabel(d) {
  if (!d || typeof d !== 'object') return '';
  if (d.uploadedVia != null && d.uploadedVia !== '') {
    const k = normalizeFileVia(d.uploadedVia);
    return k === 'other' ? 'Other' : FILE_ADD_VIA[k];
  }
  if (d.source === 'claude-upload-link') return FILE_ADD_VIA.claude_link;
  if (d.uploadedFrom === 'ap-billing') return FILE_ADD_VIA.ticket_edit;
  return '';
}

/**
 * Stamp a NEW file entry. Returns a copy with uploadedVia always set and
 * uploadedBy only when there is a provable uploader (buildTicketDocUploader
 * output; null = store none). Never touches uploadedAt or anything else.
 */
export function stampFileProvenance(entry, uploader, via) {
  const out = { ...(entry || {}) };
  out.uploadedVia = normalizeFileVia(via);
  if (uploader && typeof uploader === 'object' && uploader.uid) {
    out.uploadedBy = { ...uploader };
  }
  return out;
}

export const FILE_ADD_TARGETS = Object.freeze(['job_permits', 'job_photos', 'ticket_documents', 'ticket_photos', 'ticket_line_photos']);

/**
 * The auditLog FILE_ADDED payload for one file (window.logAudit adds category,
 * action, timestamp and the rule-pinned `user`). `uid` must be the auth uid —
 * firestore.rules refuses any other. No download URL: it is a bearer token.
 * Undefined keys are dropped by logAudit.
 */
export function fileAddedAuditPayload({ entry, target, via, jobId, ticketId, ticketNo, ticketLineId, jobTitle, uid } = {}) {
  const e = entry || {};
  const s = (v, n) => (v == null || v === '' ? undefined : String(v).slice(0, n));
  return {
    target: FILE_ADD_TARGETS.includes(target) ? target : 'other',
    via: normalizeFileVia(via),
    jobId: s(jobId, 128),
    ticketId: s(ticketId, 128),
    ticketNo: s(ticketNo, 40),
    ticketLineId: s(ticketLineId, 128),
    jobTitle: s(jobTitle, 120),
    fileId: s(e.id, 128),
    fileName: s(e.name, 200),
    fullPath: s(e.fullPath || e.path, 512),
    mimeType: s(e.mimeType, 80),
    sizeBytes: typeof e.sizeBytes === 'number' ? e.sizeBytes : undefined,
    uid: s(uid, 128),
  };
}

/**
 * Storage customMetadata for an upload. storage.rules pins uploadedByUid to the
 * caller when present; {} when there is no uid (the upload still goes).
 */
export function fileUploadCustomMetadata(uid, via) {
  const u = String(uid == null ? '' : uid).trim();
  if (!u) return {};
  return { uploadedByUid: u.slice(0, 128), uploadedVia: normalizeFileVia(via) };
}

/**
 * "Added by Sean Wood · via Driver app · Oct 14, 1:49 PM" — plain text (caller
 * escapes). '' when the row records neither who nor how (old rows). A stamp
 * with no name reads "a signed-in user" rather than leaking a uid onto the
 * screen. `formatTime(iso)` is optional; without it, no time is shown.
 */
export function ticketDocUploadedLine(d, formatTime) {
  if (!d || typeof d !== 'object') return '';
  const by = d.uploadedBy;
  const hasBy = !!(by && typeof by === 'object' && by.uid);
  const via = fileViaLabel(d);
  if (!hasBy && !via) return '';
  const parts = [];
  if (hasBy) parts.push(`Added by ${String(by.name || '').trim() || 'a signed-in user'}`);
  if (via) parts.push(hasBy ? `via ${via}` : `Added via ${via}`);
  if (d.uploadedAt && typeof formatTime === 'function') {
    let when = '';
    try { when = String(formatTime(d.uploadedAt) || ''); } catch (_) { when = ''; }
    if (when) parts.push(when);
  }
  return parts.join(' · ');
}

/** Same line, under its newer name. */
export const fileAddedLine = ticketDocUploadedLine;
